import type { GuideCampaignConfig } from './types';

/** The city overrides below are hand-written copy for the launch cities, whose images ship in client/public. */
const cityImage = (name: string) => `/images/destinations/${name.toLowerCase()}.jpg`;

const DEFAULT_BENEFITS = [
  { title: 'Share expertise', description: 'Authentic local experiences' },
  { title: 'Meet travelers', description: 'Visitors from around the world' },
  { title: 'Grow your profile', description: 'Showcase your knowledge' },
  { title: 'Get discovered', description: 'When travelers search your city' },
] as const;

export const guideRecruitmentCampaign: GuideCampaignConfig = {
  id: 'guide-recruitment-launch-2026',
  enabled: import.meta.env.VITE_GUIDE_RECRUITMENT_CAMPAIGN !== 'false',
  delayMs: { min: 5000, max: 8000 },
  dismissalCooldownDays: 7,
  default: {
    overline: 'Guide with Trplin',
    headline: 'Be the local guide travelers are looking for.',
    description:
      'Share your knowledge, introduce travelers to authentic local experiences, and grow your guide profile on Trplin.',
    benefits: [...DEFAULT_BENEFITS],
    cta: 'Become a Trplin Guide',
    secondaryCta: 'Maybe later',
    image: '/images/destinations/jaipur.jpg',
    imageAlt: 'Hawa Mahal and local street life in Jaipur — authentic India experiences',
  },
  continueVariant: {
    overline: 'Your application',
    headline: 'Continue your guide application',
    description:
      'Pick up where you left off — complete your profile and submit when you are ready.',
    cta: 'Continue application',
    image: '/images/destinations/choose-guide.jpg',
    imageAlt: 'Continue building your Trplin guide profile',
  },
  cityOverrides: {
    Agra: {
      headline: 'Know Agra beyond the Taj Mahal?',
      description:
        'Share hidden lanes, local stories, and Mughal heritage with travelers visiting Agra.',
      image: cityImage('Agra'),
      imageAlt: 'Agra — Taj Mahal and Mughal heritage',
    },
    Delhi: {
      headline: 'Know Delhi like a local?',
      description:
        'From Old Delhi lanes to hidden monuments — help travelers experience the capital authentically.',
      image: cityImage('Delhi'),
      imageAlt: 'Delhi — Old Delhi lanes and monuments',
    },
    Varanasi: {
      headline: 'Know Varanasi beyond the guidebooks?',
      description:
        'Share Ganga ghats, spiritual walks, and the soul of Banaras with curious travelers.',
      image: cityImage('Varanasi'),
      imageAlt: 'Varanasi — Ganga ghats and spiritual walks',
    },
    Mathura: {
      headline: 'Know Mathura like a local?',
      description:
        'Guide travelers through Krishna birthplace, temple trails, and the heart of Braj.',
      image: cityImage('Mathura'),
      imageAlt: 'Mathura — Krishna birthplace and temples',
    },
    Vrindavan: {
      headline: 'Know Vrindavan like a local?',
      description:
        'Share temple trails, bhajan evenings, and the devotional spirit of Vrindavan.',
      image: cityImage('Vrindavan'),
      imageAlt: 'Vrindavan — temple trails and bhajan evenings',
    },
    Jaipur: {
      headline: 'Know Jaipur like a local?',
      description:
        'Introduce travelers to Pink City forts, bazaars, and the Rajasthan you know best.',
      image: cityImage('Jaipur'),
      imageAlt: 'Jaipur — Pink City forts and bazaars',
    },
  },
};

export function shouldForceGuideRecruitmentCampaign(): boolean {
  return import.meta.env.VITE_FORCE_GUIDE_RECRUITMENT_CAMPAIGN === 'true';
}

export function getCampaignDelayMs(config: GuideCampaignConfig): number {
  if (shouldForceGuideRecruitmentCampaign() || import.meta.env.DEV) {
    return 2000;
  }
  const { min, max } = config.delayMs;
  if (min >= max) return min;
  return Math.floor(min + Math.random() * (max - min + 1));
}
